import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import config from "config";
import { currentUserQueryKey } from "queries/current-user";
import {
  activeSchoolKey,
  authPortalAccessClearedEvent,
  authRoleClearedEvent,
  authSessionClearedEvent,
  authSessionUpdatedEvent,
  authRoleKey,
  clearAuthSession,
  getSessionRefreshDelayMs,
  normalizeUserRole,
  persistAuthSession,
  portalAccessKey,
  readStoredAuthSession,
  refreshAuthSession,
} from "services/auth-session.service";
import { syncAuthNetworkAccessToken } from "services/sub-services/auth-network-service/auth-network.service";
import { PortalAccessProfile } from "types/api";
import { clearSchoolBrandThemeOverrides } from "utils/school-theme";

const schoolThemePreferenceKey = "athspire_school_theme_preference";

const resetBrandThemeForNewLogin = () => {
  clearSchoolBrandThemeOverrides();
};

export type SchoolThemePreference = "school_brand" | "classic";
type LoginPayload = {
  accessToken: string;
  refreshToken?: string | null;
  role?: string | null;
  portalAccess?: PortalAccessProfile | null;
};

interface AuthContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  isReady: boolean;
  userRole: string | null;
  portalAccess: PortalAccessProfile | null;
  schoolThemePreference: SchoolThemePreference;
  activeSchoolId: string | null;
  login: (payload: LoginPayload) => void;
  logout: () => void;
  setUserRole: (role?: string | null) => void;
  setPortalAccess: (portalAccess?: PortalAccessProfile | null) => void;
  setSchoolThemePreference: (preference: SchoolThemePreference) => void;
  setActiveSchoolId: (schoolId?: string | number | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userRole, setUserRoleState] = useState<string | null>(null);
  const [portalAccess, setPortalAccessState] = useState<PortalAccessProfile | null>(null);
  const [schoolThemePreference, setSchoolThemePreferenceState] =
    useState<SchoolThemePreference>("school_brand");
  const [activeSchoolId, setActiveSchoolIdState] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const syncAuthStateFromStorage = () => {
      const { accessToken: token, role, portalAccess } = readStoredAuthSession();

      syncAuthNetworkAccessToken(token);
      setAccessToken(token);
      setUserRoleState(role);
      setPortalAccessState(portalAccess);
    };

    syncAuthStateFromStorage();

    const storedSchoolThemePreference = localStorage.getItem(schoolThemePreferenceKey);
    const storedActiveSchoolId = localStorage.getItem(activeSchoolKey);
    if (
      storedSchoolThemePreference === "classic" ||
      storedSchoolThemePreference === "school_brand"
    ) {
      setSchoolThemePreferenceState(storedSchoolThemePreference);
    }
    if (storedActiveSchoolId) {
      setActiveSchoolIdState(storedActiveSchoolId);
    }

    const handleSessionCleared = () => setAccessToken(null);
    const handleRoleCleared = () => setUserRoleState(null);
    const handlePortalAccessCleared = () => setPortalAccessState(null);
    const handleSessionUpdated = () => syncAuthStateFromStorage();
    const handleStorage = (event: StorageEvent) => {
      if (
        !event.key ||
        [
          config.accessTokenKey,
          config.refreshTokenKey,
          authRoleKey,
          portalAccessKey,
          activeSchoolKey,
        ].includes(event.key)
      ) {
        syncAuthStateFromStorage();

        if (event.key === activeSchoolKey) {
          setActiveSchoolIdState(localStorage.getItem(activeSchoolKey));
        }
      }
    };

    window.addEventListener(authSessionClearedEvent, handleSessionCleared);
    window.addEventListener(authRoleClearedEvent, handleRoleCleared);
    window.addEventListener(authPortalAccessClearedEvent, handlePortalAccessCleared);
    window.addEventListener(authSessionUpdatedEvent, handleSessionUpdated);
    window.addEventListener("storage", handleStorage);
    setIsReady(true);

    return () => {
      window.removeEventListener(authSessionClearedEvent, handleSessionCleared);
      window.removeEventListener(authRoleClearedEvent, handleRoleCleared);
      window.removeEventListener(authPortalAccessClearedEvent, handlePortalAccessCleared);
      window.removeEventListener(authSessionUpdatedEvent, handleSessionUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const { refreshToken } = readStoredAuthSession();

    if (!refreshToken) {
      return;
    }

    const refreshDelayMs = getSessionRefreshDelayMs(accessToken);

    if (refreshDelayMs === null) {
      return;
    }

    const refreshTimeoutId = window.setTimeout(() => {
      void Promise.resolve(refreshAuthSession()).catch(() => undefined);
    }, refreshDelayMs);

    return () => window.clearTimeout(refreshTimeoutId);
  }, [accessToken]);

  const setUserRole = useCallback((role?: string | null) => {
    const normalizedRole = normalizeUserRole(role);

    setUserRoleState((current) => {
      if (current === normalizedRole) {
        return current;
      }

      persistAuthSession({ role: normalizedRole });
      return normalizedRole;
    });
  }, []);

  const setPortalAccess = useCallback((value?: PortalAccessProfile | null) => {
    const nextPortalAccess = value || null;
    const serializedNext = nextPortalAccess ? JSON.stringify(nextPortalAccess) : null;

    setPortalAccessState((current) => {
      const serializedCurrent = current ? JSON.stringify(current) : null;

      if (serializedCurrent === serializedNext) {
        return current;
      }

      persistAuthSession({ portalAccess: nextPortalAccess });
      return nextPortalAccess;
    });
  }, []);

  const setSchoolThemePreference = (preference: SchoolThemePreference) => {
    localStorage.setItem(schoolThemePreferenceKey, preference);
    setSchoolThemePreferenceState(preference);
  };

  const setActiveSchoolId = useCallback((schoolId?: string | number | null) => {
    const normalizedSchoolId = schoolId ? String(schoolId) : null;

    setActiveSchoolIdState((current) => {
      if (current === normalizedSchoolId) {
        return current;
      }

      if (normalizedSchoolId) {
        localStorage.setItem(activeSchoolKey, normalizedSchoolId);
      } else {
        localStorage.removeItem(activeSchoolKey);
      }

      return normalizedSchoolId;
    });
  }, []);

  const login = ({ accessToken: token, refreshToken, role, portalAccess }: LoginPayload) => {
    resetBrandThemeForNewLogin();
    queryClient.clear();
    localStorage.removeItem(activeSchoolKey);
    setActiveSchoolIdState(null);
    persistAuthSession({
      accessToken: token,
      refreshToken,
      role,
      portalAccess,
    });
    syncAuthNetworkAccessToken(token);
    setAccessToken(token);
    setUserRoleState(normalizeUserRole(role));
    setPortalAccessState(portalAccess || null);
    void queryClient.invalidateQueries({ queryKey: currentUserQueryKey });
  };

  const logout = () => {
    queryClient.clear();
    clearAuthSession({ clearActiveSchool: false });
    syncAuthNetworkAccessToken(null);
    setAccessToken(null);
    setUserRoleState(null);
    setPortalAccessState(null);
    // Keep activeSchoolId in memory and localStorage so login / pre-auth screens can
    // continue applying the last known school brand theme after sign-out.
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!accessToken,
        accessToken,
        isReady,
        login,
        logout,
        activeSchoolId,
        schoolThemePreference,
        setActiveSchoolId,
        setSchoolThemePreference,
        setUserRole,
        portalAccess,
        setPortalAccess,
        userRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
